import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogInIcon, MailIcon, LockIcon, AlertCircleIcon } from 'lucide-react';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const Login: React.FC = () => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  
  // Get redirect path from navigation state
  const from = location.state?.from?.pathname || '/subscription';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    
    // Clear any existing session before login
    await supabase.auth.signOut();

    try {
      setError('');
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      setError(message.includes('Invalid login') ? 'Invalid email or password' : message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 to-primary-700 flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="w-full max-w-md"
      >
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-white/10 p-3 rounded-full">
              <LogInIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Sign In</h1>
          <p className="text-white/70">Welcome back to VoiceVision</p>
        </div>

        {/* Form Container */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-white/20">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-error-500/20 border border-error-500/30 text-error-100 p-4 mb-6 rounded-lg backdrop-blur-sm"
            >
              <div className="flex items-center">
                <AlertCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                Email Address
               <span className="text-error-300 ml-1" aria-label="required">*</span>
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                autoComplete="email"
                leftIcon={<MailIcon className="h-5 w-5 text-gray-400" />}
                className="bg-white/90 border-white/20 text-gray-900 placeholder-gray-500 focus:bg-white focus:border-primary-500"
                fullWidth
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                Password
               <span className="text-error-300 ml-1" aria-label="required">*</span>
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                leftIcon={<LockIcon className="h-5 w-5 text-gray-400" />}
                className="bg-white/90 border-white/20 text-gray-900 placeholder-gray-500 focus:bg-white focus:border-primary-500"
                fullWidth
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                 id="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded bg-white/90"
                />
                <span className="ml-2 text-sm text-white/80" htmlFor="remember-me">Remember me</span>
              </label>
              <Link 
               aria-label="Reset your password"
                to="/forgot-password" 
                className="text-sm font-medium text-primary-300 hover:text-white transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
             aria-label={loading ? 'Signing in, please wait' : 'Sign in to your account'}
              variant="primary"
              fullWidth
              isLoading={loading}
              leftIcon={<LogInIcon className="h-5 w-5" />}
              className="bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white font-medium py-3 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-8 text-center">
            <p className="text-white/70">
              Don't have an account?{' '}
              <Link 
               aria-label="Create a new account"
                to="/signup" 
                className="font-medium text-primary-300 hover:text-white transition-colors"
              >
                Create one here
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} VoiceVision. All rights reserved.
          </p>
          <div className="flex justify-center space-x-6 mt-4">
            <Link to="/privacy" className="text-white/50 hover:text-white/70 text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-white/50 hover:text-white/70 text-sm transition-colors">
              Terms of Service
            </Link>
            <a href="mailto:support@aichaperones.com" className="text-white/50 hover:text-white/70 text-sm transition-colors">
              Contact
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;